// API da Conferência de recebimento de NF-e — espelha backend/Controllers/ConferenciaNfeController.cs
// (reconstrução 2026-07 sobre Postgres). 1 método por endpoint; nenhum envelope {success,data} —
// o controller devolve Ok(entidade)/Ok(lista) crus, então cada método aqui expõe a AxiosResponse<T>
// direto (os hooks acessam response.data, sem .data.data como em xmlTerceiros/cte).
import api from './client';
import type {
  AtualizarCabecalhoConferenciaRequest,
  AtualizarItemConferenciaRequest,
  ConcluirConferenciaRequest,
  ConferenciaListItem,
  ConferenciaNfe,
  ConferenciaNfeFoto,
  ConferenciaNfeItem,
  IniciarConferenciaRequest,
  ListarConferenciasFiltros,
  ProdutoPorBarras,
} from '../types/conferencia';

export const conferenciaApi = {
  /** Lista cabeçalhos + resumo de divergência, com filtros opcionais. */
  listar: (filtros: ListarConferenciasFiltros = {}) => {
    const params = new URLSearchParams();
    if (filtros.status) params.append('status', filtros.status);
    if (filtros.periodoIni) params.append('periodoIni', filtros.periodoIni);
    if (filtros.periodoFim) params.append('periodoFim', filtros.periodoFim);
    if (filtros.setor) params.append('setor', filtros.setor);
    if (filtros.busca) params.append('busca', filtros.busca);
    if (filtros.pendencia) params.append('pendencia', filtros.pendencia);

    const queryString = params.toString();
    return api.get<ConferenciaListItem[]>(`/conferencia-nfe${queryString ? `?${queryString}` : ''}`);
  },

  /** Detalhe (cabeçalho + itens) de uma conferência. */
  obter: (id: number) => api.get<ConferenciaNfe>(`/conferencia-nfe/${id}`),

  /** Edita setor de recebimento e/ou observação do cabeçalho — update parcial (null = não altera). */
  atualizarCabecalho: (id: number, body: AtualizarCabecalhoConferenciaRequest) =>
    api.put<ConferenciaNfe>(`/conferencia-nfe/${id}`, body),

  /** Inicia (ou recupera, se já existir) a conferência de uma NF-e. */
  iniciar: (dados: IniciarConferenciaRequest) => api.post<ConferenciaNfe>('/conferencia-nfe', dados),

  /** Bipagem/contagem de 1 item — chamada a cada leitura de código de barras. */
  atualizarItem: (id: number, itemId: number, body: AtualizarItemConferenciaRequest) =>
    api.put<ConferenciaNfeItem>(`/conferencia-nfe/${id}/item/${itemId}`, body),

  /** Conclui a conferência, opcionalmente declarando pendência. Body inteiro é opcional (omitido = sem pendência). */
  concluir: (id: number, body?: ConcluirConferenciaRequest) =>
    api.post<ConferenciaNfe>(`/conferencia-nfe/${id}/concluir`, body ?? {}),

  reabrir: (id: number) => api.post<ConferenciaNfe>(`/conferencia-nfe/${id}/reabrir`),

  remover: (id: number) => api.delete<void>(`/conferencia-nfe/${id}`),

  // ── Fotos (anexo) ────────────────────────────────────────────────────

  /** Metadados das fotos da conferência (sem os bytes). */
  listarFotos: (id: number) => api.get<ConferenciaNfeFoto[]>(`/conferencia-nfe/${id}/anexo`),

  /** Anexa uma foto (multipart) à conferência. */
  enviarFoto: (id: number, arquivo: File) => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    return api.post<ConferenciaNfeFoto>(`/conferencia-nfe/${id}/anexo`, form);
  },

  /**
   * Bytes da foto via axios autenticado (Bearer no header, não cookie) — mesmo idiom de
   * xmlTerceirosApi.baixarDanfe/baixarXml. GET /anexo/{id} é protegido por policy; um
   * `<img src>` cru não reenvia o Authorization header e daria 401. Diferente do XML/DANFE,
   * aqui NÃO dispara download — quem chama (galeria, Task 16) monta o object URL e é
   * responsável por revogá-lo (URL.revokeObjectURL) quando a imagem sai de cena.
   */
  baixarFoto: async (anexoId: number): Promise<Blob> => {
    const response = await api.get(`/conferencia-nfe/anexo/${anexoId}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  removerFoto: (anexoId: number) => api.delete<void>(`/conferencia-nfe/anexo/${anexoId}`),

  /**
   * Foto do PRODUTO (não da conferência) pro checklist da doca. Blob via axios autenticado
   * pelo mesmo motivo de `baixarFoto`: o endpoint está sob a policy `conferencia_nfe` e um
   * `<img src>` cru não reenvia o Bearer. 404 = produto sem foto cadastrada — quem chama
   * (useImagemProduto) trata como placeholder, não como erro.
   */
  baixarImagemProduto: async (codigo: string): Promise<Blob> => {
    const response = await api.get(`/conferencia-nfe/produto/${encodeURIComponent(codigo)}/imagem`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /** Resolve um código de barras bipado pro produto interno (TESTPRODUTOGERAL.CODIGOBARRA). */
  produtoPorBarras: (ean: string) =>
    api.get<ProdutoPorBarras>(`/conferencia-nfe/produto/barras/${encodeURIComponent(ean)}`),
};

export default conferenciaApi;
